/**
 * Gmail compose URL builder + hook.
 *
 * Used on the coach console to open Gmail's compose window pre-filled with
 * a templated outreach email. Behavior diverges by platform:
 *
 *   DESKTOP — uses Gmail's web compose URL (mail.google.com/mail/?view=cm…).
 *             `authuser=` pins the sender so each coach composes from THEIR
 *             signed-in MaxSC email.
 *
 *   MOBILE  — uses the Gmail app deep link (googlegmail://co?…) so the
 *             subject + body actually pre-fill in the compose screen.
 *             Gmail's mobile WEB UI silently drops the URL params during the
 *             redirect from browser → mobile site, so the deep link is the
 *             only way to keep the pre-render on phones. Requires the Gmail
 *             app to be installed (~all MaxSC parents have it).
 *
 * The public footer keeps `mailto:` so visitors aren't forced to use Gmail.
 */
import { useAuth } from "@/context/AuthContext";

function isMobileDevice() {
    if (typeof navigator === "undefined") return false;
    return /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);
}

export function gmailComposeUrl(to, { subject, body, authuser } = {}) {
    if (!to) return "#";

    // Mobile → Gmail app deep link. Compose params pre-fill on both iOS and
    // Android Gmail apps. Param keys differ from the web URL (subject vs su).
    // IMPORTANT: build the query string with encodeURIComponent (not
    // URLSearchParams) so spaces become %20, not `+`. The Gmail iOS deep
    // link parser does NOT decode `+` back to space — it would render
    // literal "+" between every word.
    if (isMobileDevice()) {
        const parts = [`to=${encodeURIComponent(to)}`];
        if (subject) parts.push(`subject=${encodeURIComponent(subject)}`);
        if (body) parts.push(`body=${encodeURIComponent(body)}`);
        return `googlegmail://co?${parts.join("&")}`;
    }

    // Desktop → Gmail web compose. `authuser` pins to the signed-in coach.
    const params = new URLSearchParams({ view: "cm", fs: "1", to });
    if (subject) params.set("su", subject);
    if (body) params.set("body", body);
    if (authuser) params.set("authuser", authuser);
    return `https://mail.google.com/mail/?${params.toString()}`;
}

// ----- Lead intro email template ----- //

const ORG_NAME = "Maximum Sports Conditioning";

function joinWithOxford(items) {
    const xs = (items || []).filter(Boolean);
    if (xs.length === 0) return "";
    if (xs.length === 1) return xs[0];
    if (xs.length === 2) return `${xs[0]} and ${xs[1]}`;
    return `${xs.slice(0, -1).join(", ")}, and ${xs[xs.length - 1]}`;
}

/**
 * Build the subject + body for the initial outreach to a lead.
 *
 * Inputs are intentionally permissive — call sites pass whatever they
 * have (full names, first-name-only, etc.) and we extract the first
 * name internally.
 */
export function buildLeadIntroEmail({
    parentName,           // "Sarah Smith" or just "Sarah"
    parentFirstName,      // optional, takes precedence over parentName
    athleteNames,         // ["Tom Smith","Jordan S."] OR ["Tom","Jordan"]
    signupForSelf = false,
    preferredDays = [],   // ["Mon","Wed"]
    coachName,            // signing coach (from auth)
} = {}) {
    const recipientFirst = (parentFirstName || (parentName || "").trim().split(/\s+/)[0] || "").trim();
    const athleteFirsts = (athleteNames || [])
        .map((n) => (n || "").trim().split(/\s+/)[0])
        .filter(Boolean);

    const greeting = recipientFirst ? `Hi ${recipientFirst},` : "Hi,";
    const intro = signupForSelf
        ? "We'd love to have you in!"
        : (athleteFirsts.length > 0
            ? `We'd love to have ${joinWithOxford(athleteFirsts)} in!`
            : "We'd love to have you in!");

    const days = (preferredDays || []).filter(Boolean);
    const daysLine = days.length > 0
        ? `You said ${joinWithOxford(days)} ${days.length === 1 ? "works" : "work"} best — does [insert time] work?`
        : "When would work best for you? Just want to confirm a time that fits.";

    const signoff = coachName
        ? `— ${coachName}, ${ORG_NAME}`
        : `— ${ORG_NAME}`;

    const body = `${greeting}

${intro} ${daysLine}

Look forward to meeting you.

${signoff}`;

    const subject = `${ORG_NAME} — Assessment time`;
    return { subject, body };
}

/**
 * Best-effort: pull preferred days from a request's "preferred_time" string.
 * After the day-picker shipped, the string looks like:
 *   "Days: Mon, Wed · Times: Morning, Afternoon"
 * Older leads have just the time portion, so this returns [] for them.
 */
export function extractPreferredDays(preferredTime) {
    if (!preferredTime) return [];
    const m = /Days:\s*([^·]+)/i.exec(preferredTime);
    if (!m) return [];
    return m[1].split(",").map((s) => s.trim()).filter(Boolean);
}

/**
 * Hook variant — returns helpers tied to the currently signed-in MaxSC user.
 *   const compose = useGmailComposeUrl();
 *   <a href={compose(lead.email)} title={compose.title} target="_blank">…</a>
 *   <a href={compose.intro(lead).url} title={compose.title}>…</a>
 */
export function useGmailComposeUrl() {
    const { user } = useAuth();
    const authuser = user?.email || undefined;
    const coachName = user?.name || undefined;
    const fn = (to, opts = {}) => gmailComposeUrl(to, { ...opts, authuser });
    fn.title = authuser ? `Compose in Gmail as ${authuser}` : "Compose in Gmail";
    fn.from = authuser;
    fn.coachName = coachName;
    /**
     * Returns { url, subject, body } pre-filled with the lead intro template,
     * personalized to the signed-in coach.
     */
    fn.intro = (lead = {}) => {
        const { subject, body } = buildLeadIntroEmail({
            parentName: lead.parent_name || lead.name,
            parentFirstName: lead.parent_first_name,
            athleteNames: (lead.athletes || []).map((a) => a.name || `${a.first_name || ""} ${a.last_name || ""}`.trim()).filter(Boolean),
            signupForSelf: lead.signup_for === "self",
            preferredDays: lead.preferred_days?.length
                ? lead.preferred_days
                : extractPreferredDays(lead.preferred_time),
            coachName,
        });
        return {
            url: gmailComposeUrl(lead.email, { subject, body, authuser }),
            subject,
            body,
        };
    };
    return fn;
}
